'use client'

import * as React from 'react'
import { useHydrated } from 'hooks/use-hydrated'
import type { Variant } from '../types'
import { getVariantExistingStyle } from '../utils/get-variant-existing-style'
import { createVariantStyleObserver } from '../utils/create-variant-style-observer'
import { useExperimentEvents } from '../hooks/use-experiment-events'

export const StaticVariant = ({
  variant,
  renderingId,
}: {
  variant: Variant
  renderingId: string
}) => {
  const isHydrated = useHydrated()
  const htmlId = `${renderingId}-${variant.id}`
  const [isVisible, setIsVisible] = React.useState(false)
  const [hasCheckedVisibility, setHasCheckedVisibility] = React.useState(false)
  const observerRef = React.useRef<MutationObserver | null>(null)
  useExperimentEvents(variant.conditions, htmlId, isVisible)

  React.useEffect(() => {
    if (!isHydrated) {
      return
    }

    setIsVisible(!!getVariantExistingStyle(variant.id, renderingId))
    setHasCheckedVisibility(true)

    if (!observerRef.current) {
      observerRef.current = createVariantStyleObserver(variant.id, renderingId, setIsVisible)
    }

    return () => observerRef.current?.disconnect()
  }, [isHydrated, variant.id, renderingId, variant.isDefault, isVisible])

  let staticVariantClasses = `!hidden inline-static-variant-element static-variant-${htmlId}`

  if (variant.isDefault) {
    staticVariantClasses += ` render-default`
  }

  const wrapperProps = {
    suppressHydrationWarning: true,
    'data-rendering-id': renderingId,
    id: htmlId,
    className: staticVariantClasses,
  }

  // Only remove non-visible variants from the DOM after the effect has
  // confirmed their visibility status. Before that check, keep the span
  // in the DOM to prevent layout shifts during hydration and remounts.
  // Removing a !hidden (display: none) element causes no layout shift.
  if (hasCheckedVisibility && !isVisible) {
    return null
  }

  return <span {...wrapperProps}>{variant.content}</span>
}
